import React from "react";
import { Anchor, Bookmark, CloudSun, Compass, Fuel, MapPin, Radio, ShieldCheck, Star, Wind } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { marinaDetailPath } from "@/lib/marinaUrls";

export default function PremiumMarinaCard({ copy, marina, ratingSummary, prefix, onPreview, onFocus }) {
  const rating = Number(ratingSummary?.average || marina.rating || 0);
  const price = marina.price_per_night ? `${marina.price_per_night} ${marina.currency || "EUR"}` : copy.askMarina;
  const protection = marina.sheltered_from_wind ? 92 : marina.protected_anchorage_nearby ? 78 : 64;

  return (
    <article onMouseEnter={() => onFocus?.(marina)} className="group overflow-hidden rounded-[1.75rem] border border-border bg-card text-foreground shadow-xl shadow-ocean/10 backdrop-blur-xl transition-colors duration-200 hover:border-primary/45">
      <div className="relative h-56 overflow-hidden bg-slate-900">
        {marina.image_url ? <img src={marina.image_url} alt={`${marina.name} marina`} loading="lazy" width="700" height="420" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="grid h-full place-items-center bg-gradient-to-br from-cyan-950 to-blue-950"><Anchor className="h-12 w-12 text-cyan-100" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
        <button type="button" className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-slate-950/70 text-white backdrop-blur hover:bg-cyan-300 hover:text-slate-950" aria-label={copy.save(marina.name)}><Bookmark className="h-5 w-5" /></button>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex flex-wrap gap-2"><Badge className="bg-white text-slate-950 hover:bg-white">{marina.flag} {marina.country}</Badge>{marina.sheltered_from_wind && <Badge className="bg-emerald-300 text-slate-950 hover:bg-emerald-300">{copy.protected}</Badge>}</div>
          <h3 className="mt-3 truncate text-2xl font-black text-white">{marina.name}</h3>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-cyan-50/80"><MapPin className="h-4 w-4" /> {marina.city}, {marina.region}</p>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <Metric icon={Star} value={rating ? rating.toFixed(1) : "—"} label={copy.rating} />
          <Metric icon={ShieldCheck} value={`${protection}%`} label={copy.protection} />
          <Metric icon={Radio} value={marina.vhf_channel || "—"} label="VHF" />
        </div>

        <div className="rounded-3xl border border-border bg-background/70 p-4">
          <div className="flex items-center justify-between gap-3"><span className="flex items-center gap-2 font-bold"><CloudSun className="h-4 w-4 text-primary" /> {copy.safeArrival}</span><span className="text-sm font-bold text-success">{copy.good}</span></div>
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground"><Wind className="h-4 w-4" /> 12–18 kt · {copy.lightSwell} · {price}</div>
        </div>

        <div className="flex flex-wrap gap-2">{(marina.facilities || []).slice(0, 4).map((item) => <Badge key={item} variant="outline" className="border-primary/20 text-primary"><Fuel className="mr-1 h-3 w-3" /> {item}</Badge>)}</div>

        <div className="grid grid-cols-2 gap-2">
          <Button asChild><Link to={marinaDetailPath(marina, prefix)}>{copy.openHub}</Link></Button>
          <Button variant="outline" onClick={() => onPreview?.(marina)}><Compass className="h-4 w-4" /> {copy.preview}</Button>
        </div>
      </div>
    </article>
  );
}

function Metric({ icon: Icon, value, label }) {
  return <div className="rounded-2xl border border-border bg-background/70 p-3"><Icon className="mx-auto h-4 w-4 text-primary" /><p className="mt-2 font-black">{value}</p><p className="text-muted-foreground">{label}</p></div>;
}